import { useCallback } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import {
  fulfillReplacement,
  getStaffCompanies,
  getStaffCompany,
  getStaffEmployee,
  getStaffEmployees,
  postTransition,
  searchStaff,
  setCompanyVisibility,
} from "@/lib/staff-api";
import type {
  CompanyResponse,
  CompanyRow,
  EmployeeDetailResponse,
  Page,
  QueueEmployee,
  SearchResponse,
  StaffEmployee,
  StaffQueueResponse,
  StaffView,
  TransitionBody,
} from "@/lib/staff-api";
import { staffCan } from "@/lib/staff-capabilities";
import { useStaffAuth } from "@/lib/use-staff-auth";

const NO_PARAMS = new URLSearchParams();

/** Every staff query key starts with "staff": the staff session end drops them
 *  all (staff-auth-provider.tsx). */
export const staffQueryKeys = {
  queue: (view: StaffView, params: string) => ["staff", "employees", view, params] as const,
  companies: (params: string) => ["staff", "companies", params] as const,
  company: (id: string, params: string) => ["staff", "company", id, params] as const,
  employee: (vendorId: string, id: string, params: string) => ["staff", "employee", vendorId, id, params] as const,
  search: (params: string) => ["staff", "search", params] as const,
  payments: (start: string, end: string) => ["staff", "payments", start, end] as const,
};

/** Staff queues change under Mark as vendors order, so staff queries refetch
 *  after 30 seconds rather than the app's 5-minute vendor default. */
export const STAFF_STALE_TIME_MS = 30 * 1000;

function useWorksQueues(): boolean {
  const { staff } = useStaffAuth();
  return staff !== null && staffCan(staff.role, "work_queues");
}

function useViewsDirectory(): boolean {
  const { staff } = useStaffAuth();
  return staff !== null && staffCan(staff.role, "view_directory");
}

/** One dashboard view with the page's list parameters. Fetches only for a
 *  staff member who works the queues. The previous page stays on screen while
 *  the next one loads. */
export function useStaffQueue(view: StaffView, params: URLSearchParams = NO_PARAMS): UseQueryResult<StaffQueueResponse> {
  return useQuery({
    queryKey: staffQueryKeys.queue(view, params.toString()),
    queryFn: () => getStaffEmployees(view, params),
    enabled: useWorksQueues(),
    staleTime: STAFF_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

/** The Companies tab, with the page's list parameters. */
export function useStaffCompanies(params: URLSearchParams = NO_PARAMS): UseQueryResult<Page<CompanyRow>> {
  return useQuery({
    queryKey: staffQueryKeys.companies(params.toString()),
    queryFn: () => getStaffCompanies(params),
    enabled: useViewsDirectory(),
    staleTime: STAFF_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

/** The header search's results. Asks nothing for fewer than two characters. */
export function useStaffSearch(params: URLSearchParams): UseQueryResult<SearchResponse> {
  const longEnough = (params.get("q") ?? "").trim().length >= 2;
  return useQuery({
    queryKey: staffQueryKeys.search(params.toString()),
    queryFn: () => searchStaff(params),
    enabled: useViewsDirectory() && longEnough,
    staleTime: STAFF_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

/** One company's page. A 404 (missing, or hidden from this viewer) is an
 *  answer, not a failure to retry. The previous page stays on screen only
 *  while the company is the same: another company's data is never shown in
 *  its place. */
export function useStaffCompany(id: string, params: URLSearchParams = NO_PARAMS): UseQueryResult<CompanyResponse> {
  return useQuery({
    queryKey: staffQueryKeys.company(id, params.toString()),
    queryFn: () => getStaffCompany(id, params),
    enabled: useViewsDirectory() && id !== "",
    staleTime: STAFF_STALE_TIME_MS,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === id ? previous : undefined),
    retry: (failures, error) => !(error instanceof ApiError && error.status === 404) && failures < 1,
  });
}

/** One employee's page. A 404 (missing, or hidden from this viewer) is an
 *  answer, not a failure to retry. Another employee's data is never shown in
 *  place of this one. */
export function useStaffEmployee(
  vendorId: string,
  id: string,
  params: URLSearchParams = NO_PARAMS,
): UseQueryResult<EmployeeDetailResponse> {
  return useQuery({
    queryKey: staffQueryKeys.employee(vendorId, id, params.toString()),
    queryFn: () => getStaffEmployee(vendorId, id, params),
    enabled: useViewsDirectory() && vendorId !== "" && id !== "",
    staleTime: STAFF_STALE_TIME_MS,
    retry: (failures, error) => !(error instanceof ApiError && error.status === 404) && failures < 1,
  });
}

/** IT hides or unhides a company; every staff list refetches, because hidden
 *  companies leave all of them. */
export function useCompanyVisibility(): UseMutationResult<{ id: string; hidden: boolean }, Error, { id: string; hidden: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) => setCompanyVisibility(id, hidden),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["staff"] }),
  });
}

/** Refetches every staff list and page that shows employees, so the rows on screen hold
 *  fresh etags after an action. The Companies directory is only marked stale:
 *  an employee action cannot change it, and it is always mounted (the header
 *  count), so waiting on it would hold every action until the whole directory
 *  rebuilds. */
export function useRefreshQueues(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    void queryClient.invalidateQueries({ queryKey: ["staff", "companies"] });
    await Promise.all(
      ["employees", "company", "search", "employee"].map((key) => queryClient.invalidateQueries({ queryKey: ["staff", key] })),
    );
  }, [queryClient]);
}

export interface TransitionVariables {
  id: string;
  body: TransitionBody;
}

type TransitionMutation = UseMutationResult<{ employee: StaffEmployee; etag: string }, Error, TransitionVariables>;

/** One employee transition. The mutation stays pending until the queues have
 *  refetched, so a row cannot act again on the etag it just spent. */
export function useStaffTransition(): TransitionMutation {
  const refresh = useRefreshQueues();
  return useMutation({
    mutationFn: ({ id, body }: TransitionVariables) => postTransition(id, body),
    onSuccess: () => refresh(),
  });
}

export interface EmployeeAction {
  mutation: TransitionMutation;
  /** Sends `body` for this row, with the row's vendor and current etag. */
  run: (body: Omit<TransitionBody, "vendorId" | "etag">) => void;
  /** Clears the refusal and refetches the queues: the row was stale. */
  reload: () => void;
}

/** A dashboard row's actions on one employee. */
export function useEmployeeAction(employee: QueueEmployee): EmployeeAction {
  const mutation = useStaffTransition();
  const refresh = useRefreshQueues();
  return {
    mutation,
    run: (body) =>
      mutation.mutate({ id: employee.id, body: { ...body, vendorId: employee.vendorId, etag: employee.etag } }),
    reload: () => {
      mutation.reset();
      void refresh();
    },
  };
}

export interface FulfillVariables {
  orderId: string;
  vendorId: string;
  employeeId: string;
}

/** "Badge made" on a replacement row. */
export function useFulfillReplacement(): UseMutationResult<void, Error, FulfillVariables> {
  const refresh = useRefreshQueues();
  return useMutation({
    mutationFn: ({ orderId, vendorId, employeeId }: FulfillVariables) =>
      fulfillReplacement(orderId, vendorId, employeeId),
    onSuccess: () => refresh(),
  });
}
